/**
 * Combo Module
 *
 * Fetch combo combination rules and compatible products for the combo
 * try-on carousel. Submission reuses TryOnModule.submit with 2+ productIds,
 * which the backend routes to the combo (collage) pipeline automatically.
 */

import type { ComboCombination, ComboProduct, TryStyleEvents } from '../types';
import type { Fetcher } from '../utils/fetcher';
import type { EventEmitter } from '../utils/emitter';

interface CombosResponse {
  success?: boolean;
  data?: { combinations?: Array<{ categories?: string[]; label?: string | null }> };
}

interface ProductsResponse {
  success?: boolean;
  data?: {
    products?: Array<{
      id?: string | number;
      title?: string;
      price?: number;
      currency?: string;
      image?: string | null;
      category?: string | null;
      tags?: string[];
    }>;
  };
}

export class ComboModule {
  constructor(
    private readonly fetcher: Fetcher,
    private readonly emitter: EventEmitter<TryStyleEvents>,
  ) {}

  /**
   * Fetch the active combo combination rules. The SDK filters these client-side
   * to the categories compatible with the current product's try-on type.
   */
  async getCombinations(retailerId?: string): Promise<ComboCombination[]> {
    const q = retailerId ? `?retailerId=${encodeURIComponent(retailerId)}` : '';
    const res = await this.fetcher.request<CombosResponse>(
      `/api/v1/wrapper/combo-tryon/combinations${q}`,
    );
    const raw = res?.data?.combinations ?? [];
    return raw
      .filter((c) => Array.isArray(c.categories) && c.categories.length > 0)
      .map((c) => ({
        categories: (c.categories as string[]).filter((x) => typeof x === 'string'),
        label: c.label ?? null,
      }));
  }

  /**
   * Fetch up to `limit` products of a given canonical garment category for the
   * combo carousel (thumbnail/name/price only — public-safe shape).
   */
  async getProducts(
    retailerId: string,
    category: string,
    limit = 10,
  ): Promise<ComboProduct[]> {
    const q = new URLSearchParams({
      retailerId,
      category,
      page: '1',
      perPage: String(limit),
    });
    const res = await this.fetcher.request<ProductsResponse>(
      `/api/v1/public/products?${q.toString()}`,
    );
    const raw = res?.data?.products ?? [];
    return raw
      .filter((p) => p && p.id != null)
      .map((p) => ({
        id: p.id as string | number,
        title: String(p.title ?? ''),
        price: typeof p.price === 'number' ? p.price : Number(p.price) || 0,
        currency: String(p.currency ?? ''),
        image: p.image ?? null,
        category: p.category ?? null,
        tags: Array.isArray(p.tags) ? p.tags : [],
      }));
  }
}
